import {
  createContext,
  useContext,
  useEffect,
  useState,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
} from 'react'

interface ThemeContextValue {
  theme: 'light' | 'dark' | 'system'
  resolvedTheme: 'light' | 'dark'
  setTheme: Dispatch<SetStateAction<'light' | 'dark' | 'system'>>
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function ThemeProvider({ children }: PropsWithChildren) {
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>(() => {
    if (typeof window === 'undefined') {
      return 'system'
    }

    const storedTheme = window.localStorage.getItem('hd-parrainage-theme')
    if (storedTheme === 'light' || storedTheme === 'dark' || storedTheme === 'system') {
      return storedTheme
    }

    return 'system'
  })

  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('light')

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const root = document.documentElement
    let transitionCleanupTimer: number | undefined

    const applyTheme = (animated = true) => {
      const nextTheme =
        theme === 'system' ? (mediaQuery.matches ? 'dark' : 'light') : theme

      window.localStorage.setItem('hd-parrainage-theme', theme)

      const applyClasses = () => {
        setResolvedTheme(nextTheme)
        root.dataset.theme = nextTheme
        root.classList.toggle('dark', nextTheme === 'dark')
      }

      if (
        animated &&
        typeof document !== 'undefined' &&
        'startViewTransition' in document
      ) {
        root.classList.add('theme-switching')
        void root.getBoundingClientRect()

        const vt = (
          document as Document & {
            startViewTransition: (cb: () => void) => { finished: Promise<void> }
          }
        ).startViewTransition(applyClasses)

        vt.finished.finally(() => root.classList.remove('theme-switching'))
      } else if (animated) {
        root.classList.add('theme-transition')
        applyClasses()
        if (transitionCleanupTimer) window.clearTimeout(transitionCleanupTimer)
        transitionCleanupTimer = window.setTimeout(() => {
          root.classList.remove('theme-transition')
        }, 260)
      } else {
        applyClasses()
      }
    }

    const handleSystemChange = () => {
      if (theme === 'system') {
        applyTheme()
      }
    }

    // Initial mount: apply without animation to avoid flash
    applyTheme(false)
    mediaQuery.addEventListener('change', handleSystemChange)

    return () => {
      mediaQuery.removeEventListener('change', handleSystemChange)
      if (transitionCleanupTimer) window.clearTimeout(transitionCleanupTimer)
      root.classList.remove('theme-transition')
    }
  }, [theme])

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme() {
  const context = useContext(ThemeContext)

  if (context === null) {
    throw new Error('useTheme must be used within ThemeProvider.')
  }

  return context
}
